import { CSSProperties, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { PublicBaseView } from "./PublicBaseView";
import { Photo } from "../models_generated";
import { useAsyncEffect } from "../utils/hooks";
import { API_STORE } from "../ApiStore";

const breadcrumbStyle: CSSProperties = {
  marginBottom: "20px",
  fontSize: "14px",
  color: "#666"
};

const containerStyle: CSSProperties = {
  maxWidth: "900px",
  margin: "0 auto"
};

const imageContainerStyle: CSSProperties = {
  textAlign: "center",
  marginBottom: "30px"
};

const imageStyle: CSSProperties = {
  maxWidth: "100%",
  height: "auto",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
};

const metadataStyle: CSSProperties = {
  marginTop: "20px"
};

const fieldStyle: CSSProperties = {
  marginBottom: "15px"
};

const labelStyle: CSSProperties = {
  fontWeight: "bold",
  color: "#555",
  marginRight: "8px"
};

const backLinkStyle: CSSProperties = {
  display: "inline-block",
  marginTop: "30px",
  padding: "10px 20px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "4px",
  textDecoration: "none",
  color: "#333"
};

export function PublicPhoto() {
  const { id } = useParams<{ id: string }>();
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const root = process.env.PUBLIC_URL || "";

  useAsyncEffect(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await API_STORE.doFetch("get", `/public/photo/${id}`, { jsonBody: null });
      setPhoto(data);
    } catch (error: any) {
      console.error("Error loading photo:", error);
      if (error.message && error.message.includes("404")) {
        setError("Fotografija nije pronađena ili nije objavljena");
      } else {
        setError("Greška pri učitavanju fotografije");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  return (
    <PublicBaseView>
      {loading ? (
        <p>Učitavanje...</p>
      ) : error ? (
        <div>
          <p>{error}</p>
          <Link to={`${root}/public`} style={backLinkStyle}>
            ← Povratak na kategorije
          </Link>
        </div>
      ) : photo ? (
        <div style={containerStyle}>
          <div style={breadcrumbStyle}>
            <Link to={`${root}/public`}>Početna</Link> &gt;{" "}
            <Link to={`${root}/public/category/${photo.category}`}>{photo.category}</Link> &gt;{" "}
            {photo.name || `Fotografija ${photo.id}`}
          </div>

          <h2>{photo.name || `Fotografija ${photo.id}`}</h2>

          <div style={imageContainerStyle}>
            <img
              src={photo.selectedImage || photo.image || photo.image2 || photo.image3}
              alt={photo.name || `Photo ${photo.id}`}
              style={imageStyle}
            />
          </div>

          <div style={metadataStyle}>
            {photo.date && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Datum:</span>
                <span>{photo.date}</span>
              </div>
            )}

            {photo.owner && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Vlasnik:</span>
                <span>{photo.owner}</span>
              </div>
            )}

            {photo.description && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Opis:</span>
                <div style={{ marginTop: "8px" }}>
                  <ReactMarkdown>{photo.description}</ReactMarkdown>
                </div>
              </div>
            )}

            {photo.keywords && (
              <div style={fieldStyle}>
                <span style={labelStyle}>Ključne riječi:</span>
                <span>{photo.keywords}</span>
              </div>
            )}
          </div>

          <Link to={`${root}/public/category/${photo.category}`} style={backLinkStyle}>
            ← Povratak na kategoriju
          </Link>
        </div>
      ) : null}
    </PublicBaseView>
  );
}
